/**
 * Scenarios – CreateScenarioWizard (question-first)
 *
 * Step 1: "What do I want to test?" — rendered question cards from templates.
 * Step 2: Name + description, then create.
 *
 * Scope (fiscalYear, departmentCode) is passed in from the page so the wizard
 * always operates against the URL-derived scope, not a hard-coded constant.
 */

import { useState } from "react";
import { AlertCircle, Check, ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateScenario,
  getListScenariosQueryKey,
} from "@workspace/api-client-react";
import type { Scenario, ScenarioQuestionTemplate } from "@workspace/api-client-react";
import { ACTOR } from "./types";
import type { ScenarioScope } from "./types";
import { DRIVER_TYPE_LABELS, suggestScenarioTitle } from "./utils";
import { QuestionPlaybook } from "./guidance";

type WizardStep = "question" | "meta";

export function CreateScenarioWizard({
  templates,
  scope,
  onSuccess,
  onCancel,
}: {
  templates: ScenarioQuestionTemplate[];
  scope: ScenarioScope;
  onSuccess: (scenario: Scenario) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<WizardStep>("question");
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createMutation = useCreateScenario();
  const qc = useQueryClient();

  const selectedTemplate = templates.find((t) => t.key === selectedKey);

  const handleSelectQuestion = (key: string) => {
    setSelectedKey(key);
    // Suggest a friendly, deterministic title derived from the management
    // question and scope (never an opaque code). Only fill when untouched.
    const tpl = templates.find((t) => t.key === key);
    if (tpl && !name) {
      setName(
        suggestScenarioTitle(
          tpl.question,
          scope.departmentCode,
          scope.fiscalYear
        )
      );
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedKey || !name.trim()) return;

    createMutation.mutate(
      {
        data: {
          companyId: scope.companyId,
          fiscalYear: scope.fiscalYear,
          departmentCode: scope.departmentCode,
          name: name.trim(),
          questionKey: selectedKey,
          description: description.trim() || undefined,
          actor: ACTOR,
        },
      },
      {
        onSuccess: (scenario) => {
          qc.invalidateQueries({
            queryKey: getListScenariosQueryKey(scope),
          });
          onSuccess(scenario);
        },
      }
    );
  };

  return (
    <div className="sc-wizard" data-testid="create-scenario-wizard">
      <div className="sc-wizard__head">
        <h3>New Scenario</h3>
        <button
          className="sc-icon-btn"
          onClick={onCancel}
          aria-label="Cancel"
          data-testid="button-close-wizard"
        >
          <X size={16} />
        </button>
      </div>

      {/* Step 1: question selection */}
      {step === "question" && (
        <div className="sc-wizard__body" data-testid="wizard-step-question">
          <div
            className="sc-wizard__prompt"
            data-testid="wizard-primary-question"
          >
            What do I want to test?
          </div>
          <p className="sc-wizard__sub">
            Select the management question that best describes the assumption
            you want to isolate. Each question determines the driver types
            available.
          </p>

          <div
            className="sc-question-grid"
            data-testid="question-template-grid"
          >
            {templates.map((tpl) => (
              <button
                type="button"
                key={tpl.key}
                className={`sc-question-card ${
                  selectedKey === tpl.key ? "sc-question-card--selected" : ""
                }`}
                onClick={() => handleSelectQuestion(tpl.key)}
                data-testid={`question-card-${tpl.key}`}
              >
                <div className="sc-question-card__q">{tpl.question}</div>
                <p className="sc-question-card__desc">{tpl.description}</p>
                <div className="sc-question-card__types">
                  {tpl.allowedDriverTypes.slice(0, 4).map((t) => (
                    <span key={t} className="sc-driver-type-chip">
                      {DRIVER_TYPE_LABELS[t]}
                    </span>
                  ))}
                  {tpl.allowedDriverTypes.length > 4 && (
                    <span className="sc-driver-type-chip sc-driver-type-chip--more">
                      +{tpl.allowedDriverTypes.length - 4}
                    </span>
                  )}
                </div>
                {selectedKey === tpl.key && (
                  <span className="sc-question-card__check">
                    <Check size={13} />
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="sc-wizard__footer">
            <button
              className="tl-btn"
              onClick={onCancel}
              data-testid="button-wizard-cancel"
            >
              Cancel
            </button>
            <button
              className="tl-btn primary"
              disabled={!selectedKey}
              onClick={() => setStep("meta")}
              data-testid="button-wizard-next"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: name + description */}
      {step === "meta" && selectedTemplate && (
        <form
          className="sc-wizard__body"
          onSubmit={handleCreate}
          data-testid="wizard-step-meta"
        >
          <div
            className="sc-wizard__selected-q"
            data-testid="wizard-selected-question"
          >
            <button
              type="button"
              className="sc-back-link"
              onClick={() => setStep("question")}
              data-testid="button-wizard-back"
            >
              <ChevronDown
                size={13}
                style={{ transform: "rotate(90deg)" }}
              />
              Change question
            </button>
            <div className="sc-question-summary">
              <span className="sc-label">Selected question</span>
              <strong>{selectedTemplate.question}</strong>
              <p>{selectedTemplate.description}</p>
            </div>
          </div>

          {/* Guidance for the selected management question */}
          <QuestionPlaybook
            questionKey={selectedTemplate.key}
            question={selectedTemplate.question}
          />

          <div className="sc-field">
            <label htmlFor="sc-name" className="sc-label">
              Scenario name <span className="sc-required">*</span>
            </label>
            <input
              id="sc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Path back to budget — FY2026 · Dept 643"
              required
              data-testid="input-scenario-name"
            />
            <span className="sc-field__hint">
              Suggested from the management question — edit it to anything clear
              and human. Avoid opaque codes.
            </span>
          </div>

          <div className="sc-field">
            <label htmlFor="sc-description" className="sc-label">
              Description
            </label>
            <textarea
              id="sc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional: describe the assumption being tested"
              rows={2}
              data-testid="input-scenario-description"
            />
          </div>

          {/* Baseline identity — shows derived scope */}
          <div
            className="sc-wizard__baseline-identity"
            data-testid="wizard-baseline-identity"
          >
            <span className="sc-label">Scope</span>
            <span>
              Current authoritative Forecast — FY{scope.fiscalYear} ·{" "}
              {scope.departmentCode}
            </span>
          </div>

          {createMutation.isError && (
            <div
              className="sc-form-error"
              role="alert"
              data-testid="create-scenario-error"
            >
              <AlertCircle size={13} />
              <span>Failed to create scenario. Please try again.</span>
            </div>
          )}

          <div className="sc-wizard__footer">
            <button
              type="button"
              className="tl-btn"
              onClick={() => setStep("question")}
              data-testid="button-wizard-back-footer"
            >
              Back
            </button>
            <button
              type="submit"
              className="tl-btn primary"
              disabled={createMutation.isPending || !name.trim()}
              data-testid="button-create-scenario"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Creating…
                </>
              ) : (
                <>Create scenario</>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
